import { useState, useCallback, useEffect, useRef } from 'react';
import { validateFileType, validateFileSize, getFileConfig, API_CONFIG } from '../config/appConfig';
import { extractionAPI } from '../services/extractionAPI';
import { toast } from 'react-hot-toast';

export const useFileUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const processingTimeoutRef = useRef(null);

  // Efecto de limpieza al desmontar
  useEffect(() => {
    const currentTimeout = processingTimeoutRef.current;
    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, []);

  const processFiles = useCallback((files) => {
    // Evitar procesamiento múltiple simultáneo
    if (isProcessing) {
      return;
    }

    // Verificar que hay archivos para procesar
    if (!files || files.length === 0) {
      return;
    }

    setIsProcessing(true);
    
    // Procesar archivos de forma síncrona
    const validFiles = [];
    const invalidFiles = [];
    const oversizedFiles = [];

    files.forEach(file => {
      if (validateFileType(file)) {
        // Verificar tamaño usando la nueva validación por tipo de archivo
        const sizeValidation = validateFileSize(file);
        if (!sizeValidation.isValid) {
          const fileType = sizeValidation.isZip ? 'ZIP' : 'archivo';
          oversizedFiles.push(`${file.name} (${sizeValidation.actualSizeMB}MB > ${sizeValidation.maxSizeMB}MB para ${fileType})`);
          return;
        }
        validFiles.push({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          progress: 0,
          status: 'pending' // pending, uploading, completed, error
        });
      } else {
        invalidFiles.push(file.name);
      }
    });

    // Verificar límites antes de actualizar usando configuración centralizada
    setUploadedFiles(currentFiles => {      
      if (currentFiles.length + validFiles.length > getFileConfig().MAX_FILES) {
        // No agregar archivos y mostrar error después
        setTimeout(() => toast.error(`No puedes subir más de ${getFileConfig().MAX_FILES} archivos en total`), 0);
        return currentFiles;
      }

      // Solo agregar archivos válidos
      if (validFiles.length > 0) {
        return [...currentFiles, ...validFiles];
      }
      
      return currentFiles;
    });

    // Mostrar mensajes de resultado después de actualizar estado
    setTimeout(() => {
      if (oversizedFiles.length > 0) {
        toast.error(`Archivos demasiado grandes: ${oversizedFiles.join(', ')}`);
      }
      
      if (invalidFiles.length > 0) {
        toast.error(`Archivos no válidos: ${invalidFiles.join(', ')}`);
      }
      
      if (validFiles.length > 0) {
        toast.success(`${validFiles.length} archivo(s) agregado(s) a la cola`);
      }
      
      setIsProcessing(false);
    }, 50);
  }, [isProcessing]);

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
  }, [processFiles]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  }, [processFiles]);

  const removeFile = useCallback((fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  }, []);

  const clearAllFiles = useCallback(() => {
    setUploadedFiles([]);
    setDragOver(false);
    setIsProcessing(false);
  }, []);

  const resetUploadState = useCallback(() => {
    setUploadedFiles([]);
    setDragOver(false);
    setIsProcessing(false);
  }, []);

  return {
    uploadedFiles,
    dragOver,
    isProcessing,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    removeFile,
    clearAllFiles,
    resetUploadState,
    setUploadedFiles // Exponer setUploadedFiles para uso externo
  };
};


export const useExtraction = (documentTypes, selectedDocumentFields, maxPagesPerPdf, clearAllFiles, onQuotaUpdate) => {
  const [extractionResults, setExtractionResults] = useState([]);
  const [extractionHistory, setExtractionHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({
    stage: '',
    progress: 0,
    currentFile: '',
    processedFiles: 0,
    totalFiles: 0
  });

  // Leer timeouts desde configuración centralizada
  const extractionTimeoutZip = API_CONFIG.TIMEOUT.ZIP;
  const extractionTimeoutDefault = API_CONFIG.TIMEOUT.DEFAULT;

  // Cargar historial desde el backend
  const loadHistory = useCallback(async () => {
    try {
      const result = await extractionAPI.getExtractionHistory();
      
      if (result.success && result.data && result.data.entries) {
        const history = result.data.entries.map(entry => {
          return {
            id: entry.id,
            documentType: entry.document_type,
            document_type_id: entry.document_type_id, // Nuevo campo con ID del tipo de documento
            filesCount: entry.files_count,
            totalPagesProcessed: entry.total_pages_processed,
            processedAt: new Date(entry.processed_at).toLocaleString('es-ES'),
            results: entry.results.map((result, idx) => {
              return {
                // Usar el formato que realmente envía el backend
                fileName: result.fileName,
                extractedData: result.data,  // Ahora result.data contendrá directamente los datos extraídos
                success: result.success,
                error: result.error
              };
            })
          };
        });
        
        setExtractionHistory(history);
      } else {
        setExtractionHistory([]);
      }
    } catch (error) {
      setExtractionHistory([]);
    }
  }, []);

  // Subida individual por archivo (progreso por archivo) con preservación de resultados parciales
  const uploadFilesIndividually = useCallback(async (selectedDocumentType, uploadedFiles, setUploadedFiles, maxPagesPerPdf = 1, extractionMethod = 'ocr_text') => {
    if (!selectedDocumentType || uploadedFiles.length === 0) {
      toast.error('Selecciona un tipo de documento y sube al menos un archivo');
      return;
    }
    
    setUploading(true);
    const resultsArray = [];
    setExtractionResults([]);
    
    // Inicializar progreso global
    setProgress({
      stage: 'Iniciando sesión de extracción...',
      progress: 0,
      currentFile: '',
      processedFiles: 0,
      totalFiles: uploadedFiles.length
    });
    
    let batchSessionId = null;
    let sessionStarted = false;
    
    try {
      // 1. Iniciar sesión de extracción por lotes para preservar resultados parciales
      const sessionResult = await extractionAPI.startBatchSession(
        selectedDocumentType, 
        uploadedFiles.length, 
        extractionMethod
      );
      
      if (!sessionResult.success) {
        throw new Error(sessionResult.error || 'No se pudo iniciar la sesión de extracción');
      }
      
      batchSessionId = sessionResult.data.session_id;
      sessionStarted = true;
      console.log(`Sesión de extracción iniciada: ${batchSessionId}`);
      
      // 2. Procesar archivos individualmente
      setProgress(prev => ({
        ...prev,
        stage: 'Procesando archivos...'
      }));
      
      let completedFiles = 0;
      
      for (let i = 0; i < uploadedFiles.length; i++) {
        const fileItem = uploadedFiles[i];
        setUploadedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f));
        setProgress(prev => ({
          ...prev,
          currentFile: fileItem.name
        }));

        try {
          const formData = new FormData();
          formData.append('file', fileItem.file);
          formData.append('document_type_id', selectedDocumentType);
          formData.append('max_pages_per_pdf', maxPagesPerPdf.toString());
          formData.append('extract_all_pages', 'false');
          formData.append('extraction_method', extractionMethod);

          // Detectar si es un archivo ZIP para timeout extendido
          const isZip = fileItem.file && fileItem.file.name && fileItem.file.name.toLowerCase().endsWith('.zip');
          const timeout = isZip ? extractionTimeoutZip : extractionTimeoutDefault;
          const isSingle = true;

          // Usar sessionId para preservar resultados parciales
          const result = await extractionAPI.extractData(
            formData,
            (progressData) => {
              setUploadedFiles(prev => prev.map(f =>
                f.id === fileItem.id
                  ? { ...f, progress: progressData.progress, status: 'uploading' }
                  : f
              ));
            },
            { isSingle, timeout, sessionId: batchSessionId }
          );

          // Procesar resultados (ya se guardan automáticamente en la sesión)
          if (result.success && Array.isArray(result.data.results) && result.data.results.length > 0) {
            setUploadedFiles(prev => prev.map(f =>
              f.id === fileItem.id ? { ...f, progress: 100, status: 'completed' } : f
            ));
            
            result.data.results.forEach((res, idx) => {
              const resultObj = {
                fileName: res.filename || fileItem.name,
                success: res.success,
                error_message: res.error_message || null,
                extractedData: res.extracted_data || {},
                pagesProcessed: res.pages_processed || 1,
                processedAt: new Date().toISOString()
              };
              resultsArray.push(resultObj);
              setExtractionResults(prev => [...prev, {
                id: Date.now() + i + idx,
                fileName: res.filename || fileItem.name,
                extractedData: res.extracted_data || {},
                processedAt: new Date().toLocaleString('es-ES'),
                pagesProcessed: res.pages_processed || 1,
                success: res.success,
                error: res.error_message || null
              }]);
            });
          } else {
            // Si no hay resultados, agregar uno de error          
            setUploadedFiles(prev => prev.map(f =>
              f.id === fileItem.id ? { ...f, status: 'error', progress: 100 } : f
            ));

            const resultObj = {
              fileName: fileItem.name,
              success: false,
              error_message: result.error || 'Sin resultados',
              extractedData: {},
              pagesProcessed: 1,
              processedAt: new Date().toISOString()
            };
            resultsArray.push(resultObj);
            setExtractionResults(prev => [...prev, {
              id: Date.now() + i,
              fileName: fileItem.name,
              extractedData: {},
              processedAt: new Date().toLocaleString('es-ES'),
              pagesProcessed: 1,
              success: false,
              error: result.error || 'Sin resultados'
            }]);
          }

        } catch (err) {
          console.error(`Error procesando archivo ${fileItem.name}:`, err);
          setUploadedFiles(prev => prev.map(f =>
            f.id === fileItem.id ? { ...f, status: 'error' } : f
          ));
          const errorObj = {
            fileName: fileItem.name,
            success: false,
            error_message: err?.message || 'Error desconocido',
            extractedData: {},
            pagesProcessed: 1,
            processedAt: new Date().toISOString()
          };
          resultsArray.push(errorObj);
          setExtractionResults(prev => [...prev, {
            id: Date.now() + i,
            fileName: fileItem.name,
            extractedData: {},
            processedAt: new Date().toLocaleString('es-ES'),
            pagesProcessed: 1,
            success: false,
            error: err?.message || 'Error desconocido'
          }]);
        } finally {
          const newCompletedFiles = completedFiles + 1;
          completedFiles = newCompletedFiles;
          setProgress(prev => ({
            ...prev,
            processedFiles: newCompletedFiles,
            progress: Math.round((newCompletedFiles / uploadedFiles.length) * 100),
          }));
        }
      }

      // 3. Finalizar sesión de extracción
      if (sessionStarted && batchSessionId) {
        try {
          const finalizeResult = await extractionAPI.finalizeBatchSession(batchSessionId, 'completada');
          if (finalizeResult.success) {
            console.log(`Sesión ${batchSessionId} finalizada exitosamente`);
          }
        } catch (finalizeError) {
          console.warn(`Error al finalizar sesión ${batchSessionId}:`, finalizeError);
        }
      }

      // 4. Mostrar mensajes de resumen
      const successCount = resultsArray.filter(r => r.success).length;
      const errorCount = resultsArray.length - successCount;
      
      if (resultsArray.length === 0) {
        toast.error('No se procesó ningún archivo.');
      } else if (errorCount === 0) {
        toast.success(`✅ Extracción completada: ${successCount} archivo(s) procesado(s) correctamente.`);
      } else if (successCount > 0) {
        toast.success(`⚠️ Extracción parcial: ${successCount} archivo(s) exitoso(s), ${errorCount} error(es). Los resultados exitosos se han guardado.`);
      } else {
        toast.error('❌ Extracción fallida: No se pudieron procesar los archivos.');
      }

    } catch (sessionError) {
      console.error('Error en la sesión de extracción:', sessionError);
      
      // Intentar finalizar sesión con estado de error si fue iniciada
      if (sessionStarted && batchSessionId) {
        try {
          await extractionAPI.finalizeBatchSession(batchSessionId, 'error');
        } catch (finalizeError) {
          console.warn(`Error al finalizar sesión en estado de error:`, finalizeError);
        }
      }
      
      toast.error(`Error en la extracción: ${sessionError.message || 'Error desconocido'}`);
    } finally {
      setUploading(false);
      
      // Recargar historial después de una extracción (exitosa o no)
      await loadHistory();

      // Notificar actualización de cuota
      if (onQuotaUpdate && typeof onQuotaUpdate === 'function') {
        onQuotaUpdate();
      }
    }

  }, [extractionTimeoutDefault, extractionTimeoutZip, loadHistory, onQuotaUpdate]);

  // Cargar historial al montar el componente
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleExtraction = useCallback(async (selectedDocumentType, uploadedFiles, setCurrentPage, extractionMethod = 'ocr_text') => {
    if (!selectedDocumentType || uploadedFiles.length === 0) {
      toast.error('Selecciona un tipo de documento y sube al menos un archivo');
      return;
    }

    setUploading(true);
    setProgress({
      stage: 'Preparando extracción...',
      progress: 0,
      currentFile: '',
      processedFiles: 0,
      totalFiles: uploadedFiles.length
    });

    try {
      // Crear FormData con los archivos y configuración
      const formData = new FormData();
      
      // Agregar archivos
      uploadedFiles.forEach((fileData) => {
        formData.append('files', fileData.file);
      });
      
      // Agregar configuración
      formData.append('document_type_id', selectedDocumentType);
      formData.append('max_pages_per_pdf', maxPagesPerPdf.toString());
      formData.append('extract_all_pages', 'false'); // Por defecto solo primeras páginas
      formData.append('extraction_method', extractionMethod);
      
      // Callback para mostrar progreso
      const onProgress = (progressData) => {
        setProgress(prev => ({
          ...prev,
          stage: progressData.stage,
          progress: progressData.progress,
          currentFile: progressData.currentFile || prev.currentFile
        }));
      };

      // Realizar la extracción
      const result = await extractionAPI.extractData(formData, onProgress);

      if (result.success) {
        const extractionData = result.data;
        
        // Procesar resultados para el formato esperado por la UI
        const processedResults = extractionData.results.map((fileResult, index) => ({
          id: Date.now() + index,
          fileName: fileResult.filename,
          extractedData: fileResult.extracted_data,
          processedAt: new Date().toLocaleString('es-ES'),
          pagesProcessed: fileResult.pages_processed || 1,
          success: fileResult.success,
          error: fileResult.error || null
        }));

        setExtractionResults(processedResults);
        
        // Recargar historial después de una extracción exitosa
        await loadHistory();

        // Limpiar la cola de archivos después de una extracción exitosa
        if (clearAllFiles && typeof clearAllFiles === 'function') {
          clearAllFiles();
        }

        // Notificar actualización de cuota
        if (onQuotaUpdate && typeof onQuotaUpdate === 'function') {
          onQuotaUpdate();
        }

        // Mostrar resultados del procesamiento
        const successCount = processedResults.filter(r => r.success).length;
        const errorCount = processedResults.length - successCount;
        
        if (errorCount === 0) {
          toast.success(
            `✅ Extracción completada: ${successCount} archivo(s) procesado(s) correctamente. ` +
            `${successCount} documento(s) consumidos de tu cuota.`
          );
        } else if (successCount > 0) {
          toast.success(
            `⚠️ Extracción parcial: ${successCount} archivo(s) exitoso(s), ${errorCount} error(es). ` +
            `${successCount} documento(s) consumidos de tu cuota.`
          );
        } else {
          toast.error(`❌ Extracción fallida: No se pudieron procesar los archivos.`);
        }
        
        // Resetear a la primera página cuando hay nuevos resultados
        if (setCurrentPage) {
          setCurrentPage(1);
        }
      } else {
        toast.error(`Error en extracción: ${result.error}`);
      }
    } catch (error) {
      toast.error('Error inesperado durante la extracción');
    } finally {
      setUploading(false);
      setProgress({
        stage: '',
        progress: 0,
        currentFile: '',
        processedFiles: 0,
        totalFiles: 0
      });
    }
  }, [maxPagesPerPdf, clearAllFiles, loadHistory, onQuotaUpdate]);

  const clearResults = useCallback(() => {
    setExtractionResults([]);
  }, []);

  const clearHistory = useCallback(async () => {
    try {
      const result = await extractionAPI.clearExtractionHistory();
      if (result.success) {
        setExtractionHistory([]);
        toast.success('Historial limpiado correctamente');
      } else {
        toast.error('Error al limpiar historial');
      }
    } catch (error) {
      toast.error('Error inesperado al limpiar historial');
    }
  }, []);

  return {
    extractionResults,
    extractionHistory,
    uploading,
    progress,
    handleExtraction,
    clearResults,
    clearHistory,
    setExtractionResults,
    uploadFilesIndividually 
  };
};
